/**
 * SSoT는 backend/src/s13/s13-types.ts — 직접 수정 금지, 백엔드 원본에서 복사.
 *
 * ─────────────────────────────────────────────────────────────
 * S-13 양식 공유 계약 (Phase 0 산출물)
 *
 * 프론트엔드·백엔드가 공유하는 단일 계약이다. 프론트엔드 사본
 * (frontend/src/components/s13/s13-types.ts)은 이 파일에서 복사하며,
 * 계약 변경은 반드시 이 파일에서 시작한다.
 *
 * 데이터 원형: 구 mdsouth_s13 앱의 territory.get_vw_s13(filter_date) 결과를
 * S13FormatService.java 규칙으로 엑셀에 배치한 것. 문자열 값(날짜 'YY-MM-DD',
 * 방문자명 앞 3글자)은 엑셀 셀에 기록되는 최종 형태 그대로 담는다.
 *
 * 조회 기준: 시작일~종료일 기간. 원본 함수는 시작일 하한만 있었으나
 * (created > 시작일 00:00), 재구현에서는 종료일 상한(당일 포함,
 * created < 종료일+1일 00:00)을 추가한다 — 확정된 신규 요구사항.
 */

/** 템플릿 시트 번호. 구역번호 천단위(1000~7999)가 시트 "N번구역"에 대응한다. */
export type S13SheetNo = 1 | 2 | 3 | 4 | 5 | 6 | 7;

/** 방문 1회 — 편집 허용 대상 (방문자명·방문일만 수정 가능) */
export interface S13Visit {
  /** 방문자명 (원본 규칙: nameVisitor1 앞 3글자, 같은 날짜 중복 시 max) */
  name: string;
  /** 방문일 'YY-MM-DD' — 엑셀 셀 기록 형식 그대로 */
  date: string;
}

/** 구역 한 블록 (엑셀 2행: 이름 행 + 날짜 행) */
export interface S13AreaRow {
  /** 구역번호 4자리 (A열). 편집 불가 — 시트·행 배치의 키 */
  areaNo: number;
  /** 구역 설명 — 화면 표시용, 엑셀에는 기록하지 않음 */
  description: string;
  /**
   * 방문 슬롯 (C/E/G/I열 순), 최대 4개.
   * 방문 없는 구역은 빈 배열. 4회 초과분은 서버 생성 시 원본 규칙으로 축약된다
   * (1~3회차 + 마지막 회차 → 4번째 슬롯).
   * B열(마지막 방문일)은 저장하지 않고 visits의 최대 date로 파생한다.
   * 기간 내 방문이 없으면 B열은 비워둔다 (원본 앱 동일).
   */
  visits: S13Visit[];
}

export interface S13Sheet {
  sheetNo: S13SheetNo;
  rows: S13AreaRow[];
}

/** 화면 편집·스냅샷 저장·엑셀 생성이 공유하는 페이로드 (S13Snapshot.payload) */
export interface S13Payload {
  /** 조회 시작일 'YYYY-MM-DD' — VisitorRecord.created > 시작일 00:00 (초과) */
  startDate: string;
  /** 조회 종료일 'YYYY-MM-DD' — created < 종료일+1일 00:00 (당일 포함) */
  endDate: string;
  sheets: S13Sheet[];
}

/** 스냅샷 유일 키: `${startDate}~${endDate}` (기간별 1건) */
export const periodKeyOf = (p: Pick<S13Payload, 'startDate' | 'endDate'>): string =>
  `${p.startDate}~${p.endDate}`;

// ─────────────────────────── API 계약 ───────────────────────────
// 전 라우트 관리자 전용: protect() + isAdmin()

/** GET /api/mdsouth/s13?start=YYYY-MM-DD&end=YYYY-MM-DD */
export interface S13GetResponse {
  /** snapshot: 저장본 반환 / generated: 방문자 데이터로 신규 생성(미저장) */
  source: 'snapshot' | 'generated';
  payload: S13Payload;
  /** 생성 중 경고 (예: 시트 범위 1000~7999 밖 구역 제외) */
  warnings: string[];
  /** source=snapshot일 때만 */
  updatedAt?: string;
  updatedBy?: string;
}

/** PUT /api/mdsouth/s13 — 스냅샷 upsert (키는 payload의 기간에서 파생) */
export interface S13SaveRequest {
  payload: S13Payload;
}
export interface S13SaveResponse {
  ok: true;
  updatedAt: string;
}

/**
 * POST /api/mdsouth/s13/export — 요청 본문은 S13SaveRequest와 동일.
 * 응답: xlsx 바이너리 (Content-Disposition attachment). 스냅샷도 함께 upsert.
 */

// ─────────────────── 순회방문 기록 (조회 시작일 자동 지정) ───────────────────
// 순회방문은 1년 2회. 최신 종료일 + 1일이 S-13 조회 시작일 기본값이 된다.
// 실사용은 endDate뿐이며 startDate~endDate 기간은 참고용 보관.

export interface CircuitVisit {
  id: number;
  /** 순회방문 시작일 'YYYY-MM-DD' — 참고용 */
  startDate: string;
  /** 순회방문 종료일 'YYYY-MM-DD' — 다음 S-13 조회 시작일 산정 기준 */
  endDate: string;
}

/** GET /api/mdsouth/s13/circuit-visits — endDate 내림차순 */
export interface CircuitVisitListResponse {
  visits: CircuitVisit[];
}

/** POST /api/mdsouth/s13/circuit-visits */
export interface CircuitVisitSaveRequest {
  startDate: string;
  endDate: string;
}

/** 'YYYY-MM-DD' 형식 검증 (start/end 쿼리 파라미터·프론트 공용) */
export const ISO_DATE_PATTERN = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;

/** 방문일 'YY-MM-DD' 형식 검증 (편집 입력 공용) */
export const VISIT_DATE_PATTERN = /^\d{2}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;
